import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  Typography,
} from '@mui/material';
import { Code, Image, Link, MusicNotes } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { IChatbotFlowModel } from '../../../data/models/chatbot';
import { FlowService } from '../../../service/FlowService';
import { useNotificationStore } from '../../../store/storeNotifications';
import { MuiSelectControlled } from '../../Form/Select';

const FlyoutSettingsSchema = z.object({
  enableChatFlyout: z.boolean(),
  contentSource: z.enum(['main-chat', 'output-node', 'both']),
  flyoutPosition: z.enum(['right', 'left']),
  flyOutDataOutputNodeId: z.string().nullable(),
  contentTypes: z.object({
    image: z.boolean(),
    code: z.boolean(),
    audio: z.boolean(),
    websites: z.boolean(),
  }),
});

type IFlyoutSettings = z.infer<typeof FlyoutSettingsSchema>;

export function FlyoutSettings({
  chatData,
  onSetFlow,
}: {
  chatData: IChatbotFlowModel;
  onSetFlow: (data: any) => void;
}) {
  const setNotification = useNotificationStore(state => state.setNotification);
  const [outputNodes, setOutputNodes] = useState([]);
  const formsMethods = useForm<IFlyoutSettings>({
    resolver: zodResolver(FlyoutSettingsSchema),
    defaultValues: {
      enableChatFlyout: false,
      flyoutPosition: 'right',
      contentSource: 'main-chat',
      flyOutDataOutputNodeId: null,
      contentTypes: {
        image: false,
        code: false,
        audio: false,
        websites: false,
      },
    },
  });

  async function handleSubmitForm(data: IFlyoutSettings) {
    try {
      if (!chatData?.id) {
        setNotification({
          type: 'error',
          message: 'Chat data not available',
        });
        return;
      }

      const response = await FlowService.updateFlow(chatData.id, {
        enableChatFlyout: data.enableChatFlyout,
        flyoutPosition: data.flyoutPosition,
        contentSource: data.contentSource,
        flyOutDataOutputNodeId: data.flyOutDataOutputNodeId,
        image: data.contentTypes.image,
        code: data.contentTypes.code,
        audio: data.contentTypes.audio,
        websites: data.contentTypes.websites,
      });

      if (onSetFlow) {
        onSetFlow(response.data);
      }

      setNotification({
        type: 'success',
        message: 'Saved successfully',
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Error saving flyout settings',
      });
    }
  }

  useEffect(() => {
    if (chatData) {
      formsMethods.reset({
        enableChatFlyout: chatData.enableChatFlyout ?? false,
        flyoutPosition:
          (chatData.flyoutPosition as 'right' | 'left') ?? 'right',
        contentSource:
          (chatData.contentSource as 'main-chat' | 'output-node' | 'both') ??
          'main-chat',
        flyOutDataOutputNodeId: chatData.flyOutDataOutputNodeId ?? null,
        contentTypes: {
          image: chatData.image ?? false,
          code: chatData.code ?? false,
          audio: chatData.audio ?? false,
          websites: chatData.websites ?? false,
        },
      });
    }
  }, [chatData]);

  useEffect(() => {
    async function getFlow() {
      const flow = await FlowService.getFlow(chatData.id);

      const outputNodes =
        flow.data.nodes?.filter(
          (node: any) =>
            node.type === 'outputObjectNode' &&
            node.id !== chatData.chatbotDataOutputNodeId,
        ) ?? [];

      setOutputNodes(outputNodes);
    }

    getFlow();
  }, [chatData.id]);

  useEffect(() => {
    if (
      outputNodes.length > 0 &&
      chatData.flyOutDataOutputNodeId &&
      (chatData.contentSource === 'both' ||
        chatData.contentSource === 'output-node')
    ) {
      const nodeExists = outputNodes.some(
        (node: any) => node.id === chatData.flyOutDataOutputNodeId,
      );
      if (nodeExists) {
        const currentValue = formsMethods.getValues('flyOutDataOutputNodeId');
        if (currentValue !== chatData.flyOutDataOutputNodeId) {
          formsMethods.setValue(
            'flyOutDataOutputNodeId',
            chatData.flyOutDataOutputNodeId,
            { shouldValidate: false },
          );
        }
      }
    }
  }, [
    outputNodes,
    chatData.flyOutDataOutputNodeId,
    chatData.contentSource,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  const contentSourceWatch = formsMethods.watch('contentSource');

  useEffect(() => {
    if (contentSourceWatch === 'main-chat') {
      formsMethods.setValue('flyOutDataOutputNodeId', null);
    }
  }, [contentSourceWatch, formsMethods]);

  return (
    <FormProvider {...formsMethods}>
      <form
        id="flyout-settings"
        onSubmit={formsMethods.handleSubmit(handleSubmitForm)}
        style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#101828',
              }}
            >
              Enable Chat Flyout
            </Typography>
            <Typography
              sx={{ fontSize: '16px', fontWeight: 400, color: '#4A5565' }}
            >
              Automatically detect and preview special content
            </Typography>
          </Box>

          <Controller
            name="enableChatFlyout"
            control={formsMethods.control}
            render={({ field }) => <Switch {...field} checked={field.value} />}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#101828',
              }}
            >
              Flyout Position
            </Typography>
            <Typography
              sx={{ fontSize: '16px', fontWeight: 400, color: '#4A5565' }}
            >
              Choose the position of the flyout
            </Typography>
          </Box>

          <Controller
            name="flyoutPosition"
            control={formsMethods.control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                sx={{ display: 'flex', flexDirection: 'row' }}
              >
                <FormControlLabel
                  value="left"
                  control={<Radio size="small" />}
                  label="Left"
                />
                <FormControlLabel
                  value="right"
                  control={<Radio size="small" />}
                  label="Right"
                />
              </RadioGroup>
            )}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#101828',
              }}
            >
              Content Source
            </Typography>
            <Typography
              sx={{ fontSize: '16px', fontWeight: 400, color: '#4A5565' }}
            >
              Choose where the flyout should pull content from
            </Typography>
          </Box>

          <Controller
            name="contentSource"
            control={formsMethods.control}
            render={({ field }) => (
              <RadioGroup
                aria-labelledby="content-source-radio-buttons-group-label"
                {...field}
                sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
              >
                <FormControlLabel
                  value="main-chat"
                  control={<Radio size="small" />}
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '16px',
                            fontWeight: 400,
                            color: '#101828',
                          }}
                        >
                          Main Chat
                        </Typography>

                        <Box
                          sx={{
                            backgroundColor: '#E7F0FF',
                            borderRadius: '8px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            fontWeight: 400,
                            color: '#3650DE',
                          }}
                        >
                          Contextual
                        </Box>
                      </Box>

                      <Typography
                        sx={{
                          fontSize: '16px',
                          fontWeight: 400,
                          color: '#4A5565',
                        }}
                      >
                        Flyout triggers when specific content types appear in
                        the main chat
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="output-node"
                  control={<Radio size="small" />}
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '16px',
                            fontWeight: 400,
                            color: '#101828',
                          }}
                        >
                          Output Node
                        </Typography>

                        <Box
                          sx={{
                            backgroundColor: '#E7F0FF',
                            borderRadius: '8px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            fontWeight: 400,
                            color: '#3650DE',
                          }}
                        >
                          All Content
                        </Box>
                      </Box>

                      <Typography
                        sx={{
                          fontSize: '16px',
                          fontWeight: 400,
                          color: '#4A5565',
                        }}
                      >
                        Display all content from a specific output node
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="both"
                  control={<Radio size="small" />}
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '16px',
                            fontWeight: 400,
                            color: '#101828',
                          }}
                        >
                          Both
                        </Typography>

                        <Box
                          sx={{
                            backgroundColor: '#E7F0FF',
                            borderRadius: '8px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            fontWeight: 400,
                            color: '#3650DE',
                          }}
                        >
                          Combined
                        </Box>
                      </Box>

                      <Typography
                        sx={{
                          fontSize: '16px',
                          fontWeight: 400,
                          color: '#4A5565',
                        }}
                      >
                        Combine content from main chat and selected output node
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            )}
          />

          {(contentSourceWatch === 'both' ||
            contentSourceWatch === 'output-node') && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#101828',
                }}
              >
                Output Node
              </Typography>
              <Typography
                sx={{ fontSize: '16px', fontWeight: 400, color: '#4A5565' }}
              >
                Select the output node to display content from
              </Typography>
              <MuiSelectControlled
                styles={{ minWidth: '200px' }}
                selectProps={{
                  name: 'flyOutDataOutputNodeId',
                  placeholder: 'Select an output node',
                }}
              >
                {outputNodes.length === 0 && (
                  <MenuItem value="" disabled>
                    No output nodes found
                  </MenuItem>
                )}
                {outputNodes.map((node: any) => (
                  <MenuItem key={node.id} value={node.id}>
                    {node.data.label}
                  </MenuItem>
                ))}
              </MuiSelectControlled>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#101828',
              }}
            >
              Main Chat Content Types
            </Typography>
            <Typography
              sx={{ fontSize: '16px', fontWeight: 400, color: '#4A5565' }}
            >
              Select which content types should trigger the flyout
            </Typography>

            <FormGroup sx={{ gap: '16px', mt: '8px' }}>
              <Box sx={{ display: 'flex', gap: '16px' }}>
                <Controller
                  name="contentTypes.image"
                  control={formsMethods.control}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ width: '190px' }}
                      control={
                        <Checkbox
                          {...field}
                          size="small"
                          checked={field.value}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: '#F3E8FF',
                              borderRadius: '999px',
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Image size={20} color="#9810FA" />
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontSize: '16px',
                                fontWeight: 400,
                                color: '#101828',
                              }}
                            >
                              Image
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '16px',
                                fontWeight: 400,
                                color: '#4A5565',
                              }}
                            >
                              Photo previews
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  )}
                />
                <Controller
                  name="contentTypes.code"
                  control={formsMethods.control}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ width: '190px' }}
                      control={
                        <Checkbox
                          {...field}
                          size="small"
                          checked={field.value}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: '#DBEAFE',
                              borderRadius: '999px',
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Code size={20} color="#155DFC" />
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontSize: '16px',
                                fontWeight: 400,
                                color: '#101828',
                              }}
                            >
                              HTML/Code
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '16px',
                                fontWeight: 400,
                                color: '#4A5565',
                              }}
                            >
                              Code blocks
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  )}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: '16px' }}>
                <Controller
                  name="contentTypes.audio"
                  control={formsMethods.control}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ width: '190px' }}
                      control={
                        <Checkbox
                          {...field}
                          size="small"
                          checked={field.value}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: '#FCE7F3',
                              borderRadius: '999px',
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <MusicNotes size={20} color="#E60076" />
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '16px',
                                fontWeight: 400,
                                color: '#101828',
                              }}
                            >
                              Audio
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '16px',
                                fontWeight: 400,
                                color: '#4A5565',
                              }}
                            >
                              Audio files
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  )}
                />

                <Controller
                  name="contentTypes.websites"
                  control={formsMethods.control}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ width: '190px' }}
                      control={
                        <Checkbox
                          {...field}
                          size="small"
                          checked={field.value}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: '#DCFCE7',
                              borderRadius: '999px',
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Link size={20} />
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '16px',
                                fontWeight: 400,
                                color: '#101828',
                              }}
                            >
                              Websites
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '16px',
                                fontWeight: 400,
                                color: '#4A5565',
                              }}
                            >
                              Link previews
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  )}
                />
              </Box>
            </FormGroup>
          </Box>
        </Box>
      </form>

      <Box display="flex" justifyContent="end" width="100%">
        <Button
          type="submit"
          form="flyout-settings"
          variant="contained"
          sx={{ width: '160px', marginTop: '8px' }}
        >
          Save changes
        </Button>
      </Box>
    </FormProvider>
  );
}
